import React from 'react';
import { ClipboardList, Truck, PackageX, Link2, AlertTriangle } from 'lucide-react';

// Dashboard: low-stock parts grouped by supplier + one-click purchase order shortcut
const RestockAlert = ({ restock, onPrepare, onLinkSupplier }) => {
  if (!restock || (!restock.suppliers?.length && !restock.orphans?.length)) return null;
  const { suppliers = [], orphans = [], target } = restock;
  return (
    <div className="bg-white rounded-lg shadow-sm border-l-4 border-[#1e3a5f] mb-8" data-testid="restock-alert">
      <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <ClipboardList size={22} className="text-[#1e3a5f] flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-semibold text-gray-900" data-testid="restock-title">
              Réassort à prévoir : {suppliers.length} fournisseur{suppliers.length > 1 ? 's' : ''}{orphans.length ? ` · ${orphans.length} pièce${orphans.length > 1 ? 's' : ''} sans fournisseur` : ''}
            </h2>
            <p className="text-sm text-gray-500">Quantités proposées pour remonter chaque pièce à {target} unités en entrepôt. Cliquez pour préparer le bon de commande pré-rempli.</p>
          </div>
        </div>
      </div>

      {suppliers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 px-5 pb-5" data-testid="restock-suppliers">
          {suppliers.map(s => (
            <div key={s.supplier_id} className="border rounded-lg p-4 flex flex-col gap-2 bg-gray-50/60" data-testid={`restock-supplier-${s.supplier_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900 flex items-center gap-2"><Truck size={15} className="text-orange-500" /> {s.supplier_name}</p>
                  <p className="text-xs text-gray-500">Délai {s.lead_time_days ?? '—'} j{s.email ? ` · ${s.email}` : ''}</p>
                </div>
                {s.out_of_stock > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded-full" data-testid={`restock-out-${s.supplier_id}`}><PackageX size={11} /> {s.out_of_stock} rupture{s.out_of_stock > 1 ? 's' : ''}</span>}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div><p className="text-[11px] text-gray-500">Pièces</p><p className="font-bold text-gray-900" data-testid={`restock-parts-${s.supplier_id}`}>{s.parts}</p></div>
                <div><p className="text-[11px] text-gray-500">Unités</p><p className="font-bold text-gray-900">{s.units}</p></div>
                <div><p className="text-[11px] text-gray-500">Estimé HT</p><p className="font-bold text-orange-600" data-testid={`restock-estimate-${s.supplier_id}`}>{s.estimated_ht.toFixed(2)} €{s.missing_price ? '*' : ''}</p></div>
              </div>
              {s.pending > 0 && <p className="text-[11px] text-blue-700" data-testid={`restock-pending-${s.supplier_id}`}>{s.pending} unité{s.pending > 1 ? 's' : ''} déjà en attente de livraison (déduites)</p>}
              <p className="text-xs text-gray-500 truncate" title={s.items.map(i => `${i.name} (stock ${i.stock}${i.pending ? `, ${i.pending} attendues` : ''} → +${i.quantity})`).join(', ')}>
                {s.items.slice(0, 3).map(i => i.name).join(', ')}{s.items.length > 3 ? ` +${s.items.length - 3}` : ''}
              </p>
              {s.missing_price > 0 && <p className="text-[11px] text-amber-700">* {s.missing_price} pièce{s.missing_price > 1 ? 's' : ''} sans prix d'achat</p>}
              <button
                type="button"
                data-testid={`restock-prepare-${s.supplier_id}`}
                onClick={() => onPrepare({ id: s.supplier_id, name: s.supplier_name, email: s.email, lead_time_days: s.lead_time_days })}
                className="mt-auto inline-flex items-center justify-center gap-2 text-sm font-medium text-white bg-[#1e3a5f] hover:bg-[#2d4a6f] rounded-md px-3 py-2"
              >
                <ClipboardList size={15} /> Préparer le bon de commande
              </button>
            </div>
          ))}
        </div>
      )}

      {orphans.length > 0 && (
        <div className="border-t px-5 py-3" data-testid="restock-orphans">
          <p className="text-xs font-semibold text-gray-600 uppercase flex items-center gap-1 mb-2"><AlertTriangle size={12} className="text-amber-500" /> Stock bas sans fournisseur lié</p>
          <ul className="flex flex-wrap gap-2">
            {orphans.map(p => (
              <li key={p.id} data-testid={`restock-orphan-${p.id}`} className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-full pl-1 pr-3 py-1">
                <img src={p.image} alt="" className="w-6 h-6 object-contain bg-white rounded-full" />
                <span className="text-gray-800">{p.name}</span>
                <span className={`text-xs font-semibold ${p.stock === 0 ? 'text-red-600' : 'text-amber-700'}`}>{p.stock === 0 ? 'rupture' : `${p.stock} rest.`}</span>
                <button type="button" data-testid={`restock-link-${p.id}`} onClick={() => onLinkSupplier(p.id)} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1"><Link2 size={11} /> Associer un fournisseur</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RestockAlert;
